import { ref, computed } from 'vue';
import { hasRolePermission, hasClientSetting } from '../common/rolePermission';

/**
 * ANCHOR ROLES (PD-1258 slice 1) — shared state + rules for both portals.
 *
 * Model: an anchor is an EVALUATED role flagged as a calibration reference. A
 * separate CEO/ceiling flag (layered on the anchor flag) fixes the top of the
 * register — classification stops offering levels at or above it, and an
 * evaluation landing there is flagged for review (advisory, never blocked).
 *
 * Scope is the CLIENT, not a levelling framework: the evaluation journey loads
 * its whole option set with `where client_id = ...` and no framework filter, and
 * an evaluation never persists a framework id. One ceiling per client.
 *
 * Gated by two client settings:
 *   levelling_anchor_roles        BOOL — the feature is visible at all
 *   levelling_anchor_roles_admin  LIST — system roles that may set/remove anchors
 * Everything here is inert while the first is off, so flag-off === the app
 * exactly as before. The server re-checks both on every write; `canGovern` here
 * is UX only.
 *
 * Vue 2.7 note: `markRaw()` this object before passing it as a prop, or the
 * observer unwraps its inner refs and `.value` writes throw.
 *
 * @param {Object} options
 * @param {{post:Function,get:Function}} options.http  axios-like client
 * @param {() => Object} options.getUser               returns the logged-in user
 * @param {(msg:string, tone?:string) => void} [options.onToast]
 */
export const ANCHOR_SETTING = 'levelling_anchor_roles';
export const ANCHOR_ADMIN_SETTING = 'levelling_anchor_roles_admin';
/** Backend default when levelling_anchor_roles_admin has no value. Keep in sync. */
export const ANCHOR_ADMIN_FALLBACK = ['RoleMapper Admin'];

export function levellingAnchors(options) {
  const { http, getUser = () => null, onToast = () => {} } = options || {};

  const self = {
    anchors: ref([]),        // [{ doc_id, isCeiling, rationale, levelCode, levelPosition, title, setByName, setDate }]
    loading: ref(false),
    saving: ref(false),
    loaded: ref(false),
    /** Server's own view of the caller's capability — authoritative once loaded. */
    serverCanGovern: ref(null),
    serverEnabled: ref(null),
    /** Is the guided setup wizard on? Server-authoritative; nested under `enabled`. */
    wizard: ref(false),
  };

  /* ---- feature gating ---------------------------------------------------- */

  // Read from the user's settings up front so gating works before the first
  // fetch resolves (the control centre renders its column model synchronously).
  self.enabled = computed(() => {
    if (self.serverEnabled.value !== null) return self.serverEnabled.value;
    return hasClientSetting(getUser(), ANCHOR_SETTING);
  });

  self.canGovern = computed(() => {
    if (!self.enabled.value) return false;
    if (self.serverCanGovern.value !== null) return self.serverCanGovern.value;
    return hasRolePermission(getUser(), ANCHOR_ADMIN_SETTING, { fallbackRoles: ANCHOR_ADMIN_FALLBACK });
  });

  /* ---- reads ------------------------------------------------------------- */

  const byDocId = computed(() => {
    const map = {};
    for (const a of self.anchors.value) map[Number(a.doc_id)] = a;
    return map;
  });
  self.byDocId = byDocId;

  /** The client's CEO ceiling, or null. */
  self.ceiling = computed(() => self.anchors.value.find((a) => a.isCeiling) || null);

  /** Anchor record for a role, or null. Always null while the feature is off. */
  self.anchorInfo = (docId) => {
    if (!self.enabled.value || docId == null) return null;
    return byDocId.value[Number(docId)] || null;
  };

  self.isAnchor = (docId) => !!self.anchorInfo(docId);
  self.isCeiling = (docId) => {
    const info = self.anchorInfo(docId);
    return !!(info && info.isCeiling);
  };

  self.count = computed(() => self.anchors.value.length);

  /**
   * Can this role BECOME an anchor? Anchors must carry a completed full
   * evaluation — a classification can't be a calibration reference. Accepts
   * either a control-centre row (with `.levelling`) or a flat shape.
   */
  self.isEligible = (row) => {
    if (!row) return false;
    const lv = row.levelling || row;
    const method = lv.method || null;
    const statusKey = lv.status_key || null;
    const evalStatus = lv.evaluation_status || null;
    const isEvaluated = method === 'evaluate';
    const isComplete = statusKey === 'levelled' || evalStatus === 'COMPLETED';
    return isEvaluated && isComplete;
  };

  self.load = async () => {
    if (!self.enabled.value) {
      self.anchors.value = [];
      self.loaded.value = true;
      return;
    }
    self.loading.value = true;
    try {
      const resp = await http.post('document/lfAnchorList', {});
      applyResponse(resp && resp.data && resp.data.Data);
    } catch (err) {
      console.error('Failed to load anchor roles:', err);
      self.anchors.value = [];
    } finally {
      self.loading.value = false;
      self.loaded.value = true;
    }
  };

  function applyResponse(data) {
    if (!data) return;
    self.anchors.value = Array.isArray(data.anchors) ? data.anchors : [];
    if (typeof data.canGovern === 'boolean') self.serverCanGovern.value = data.canGovern;
    if (typeof data.enabled === 'boolean') self.serverEnabled.value = data.enabled;
    if (typeof data.wizard === 'boolean') self.wizard.value = data.wizard;
  }

  /* ---- writes ------------------------------------------------------------ */

  /**
   * The single mutator, mirroring the server's saveLfAnchor.
   * @param {number} docId
   * @param {{anchor?:boolean, ceiling?:boolean, rationale?:string}} patch
   * @returns {Promise<boolean>} true when the change was applied
   */
  self.setAnchor = async (docId, patch) => {
    if (!self.canGovern.value || docId == null) return false;
    self.saving.value = true;
    try {
      const resp = await http.post('document/saveLfAnchor', { doc_id: Number(docId), ...(patch || {}) });
      const payload = resp && resp.data;
      if (payload && payload.Status === 'Error') {
        onToast(payload.Message || 'Could not change the anchor role.', 'warn');
        return false;
      }
      // The endpoint returns the refreshed list, so no second round-trip.
      applyResponse(payload && payload.Data);
      return true;
    } catch (err) {
      console.error('Failed to save anchor role:', err);
      onToast('Could not change the anchor role.', 'warn');
      return false;
    } finally {
      self.saving.value = false;
    }
  };

  /** Anchor audit events for one role, newest first. */
  /**
   * Apply a whole set of picks in one go — the setup wizard's confirm step.
   *
   * Sequential rather than parallel, deliberately: the CEO pick may MOVE the
   * ceiling, and "one ceiling per client" is enforced by a unique index, so two
   * concurrent writes racing for that slot would have one of them rejected. The
   * ceiling pick goes first for the same reason.
   *
   * @param {Array} picks [{ docId, ceiling?, pending?, rationale? }]
   * @returns {{ ok: number, failed: Array }} per-pick outcome, so the wizard can
   *          report partial success rather than claiming everything worked.
   */
  self.setAnchorBulk = async (picks, onProgress) => {
    const list = Array.isArray(picks) ? picks : [];
    const ordered = [...list].sort((a, b) => (b.ceiling ? 1 : 0) - (a.ceiling ? 1 : 0));
    let ok = 0;
    const failed = [];
    // One request per pick, so progress is genuinely determinate — the caller can
    // count rather than show an indefinite spinner over a dozen round-trips.
    if (onProgress) onProgress(0, ordered.length);
    for (const p of ordered) {
      // eslint-disable-next-line no-await-in-loop
      const done = await self.setAnchor(p.docId, {
        anchor: true,
        ...(p.ceiling ? { ceiling: true } : {}),
        ...(p.pending ? { pending: true } : {}),
        ...(p.rationale !== undefined ? { rationale: p.rationale } : {}),
      });
      if (done) ok++; else failed.push(p);
      // Counts attempts, not successes: it tracks how far through the batch we
      // are, and a failure still consumed a round-trip.
      if (onProgress) onProgress(ok + failed.length, ordered.length);
    }
    return { ok, failed };
  };

  /**
   * Remove many anchors at once — the counterpart to the setup wizard, which can
   * only add. One request, audited per role server-side.
   *
   * @param {Object} opts
   * @param {number[]} [opts.docIds]  explicit set
   * @param {boolean}  [opts.all]     every active anchor for the client
   * @returns {Promise<number|null>} how many were removed, or null on failure
   */
  self.removeAnchors = async ({ docIds, all } = {}) => {
    if (!self.canGovern.value) return null;
    // `all` must be explicit: an empty list is never "everything".
    if (!all && !(Array.isArray(docIds) && docIds.length)) return null;
    self.saving.value = true;
    try {
      const body = all ? { all: true } : { doc_ids: docIds.map(Number) };
      const resp = await http.post('document/removeLfAnchors', body);
      const data = resp && resp.data;
      if (!data || data.Status !== 'OK') {
        onToast((data && data.Data) || 'Could not remove the anchor roles');
        return null;
      }
      applyResponse(data.Data);
      return Number((data.Data && data.Data.removed) || 0);
    } catch (err) {
      console.error('Failed to remove anchor roles:', err);
      onToast('Could not remove the anchor roles');
      return null;
    } finally {
      self.saving.value = false;
    }
  };

  self.history = async (docId) => {
    if (!self.enabled.value || docId == null) return [];
    try {
      const resp = await http.get(`document/lfAnchorHistory/${Number(docId)}`);
      const data = resp && resp.data;
      const rows = Array.isArray(data) ? data : (data && data.Data) || [];
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      console.error('Failed to load anchor history:', err);
      return [];
    }
  };

  /* ---- the ceiling rules ------------------------------------------------- */

  /**
   * Levels offered during CLASSIFICATION: everything strictly below the ceiling.
   *
   * Fails open — if filtering would leave the track empty, the full ladder is
   * returned rather than a dead end. Never applied to the levels used for
   * score → band mapping, so a full evaluation can still LAND above the ceiling
   * and be flagged for review.
   *
   * @param {Array} trackLevels rows carrying a numeric `position` (or `level`)
   */
  self.levelsBelowCeiling = (trackLevels) => {
    const list = Array.isArray(trackLevels) ? trackLevels : [];
    const ceil = self.enabled.value ? self.ceiling.value : null;
    const cap = ceil && ceil.levelPosition != null ? Number(ceil.levelPosition) : null;
    if (cap == null || isNaN(cap) || !list.length) return list;
    const below = list.filter((l) => {
      const pos = positionOf(l);
      return pos == null ? true : pos < cap;
    });
    return below.length ? below : list;
  };

  /** True when the ceiling actually removed something from this track. */
  self.ceilingApplies = (trackLevels) => {
    const list = Array.isArray(trackLevels) ? trackLevels : [];
    const filtered = self.levelsBelowCeiling(list);
    return filtered.length > 0 && filtered.length < list.length;
  };

  /**
   * Does an outcome land at or above the ceiling? Drives the advisory review
   * flag on the summary. Self-excludes the ceiling holder BY doc_id.
   *
   * @param {number} levelPosition the outcome's lf_level.position, or null when
   *        nothing is selected yet — never flagged, see below
   * @param {number} [docId]       the role being evaluated
   */
  self.isAtOrAboveCeiling = (levelPosition, docId) => {
    const ceil = self.enabled.value ? self.ceiling.value : null;
    if (!ceil || ceil.levelPosition == null) return false;
    if (docId != null && Number(ceil.doc_id) === Number(docId)) return false;
    /* Guard null/'' EXPLICITLY: Number(null) is 0, not NaN, so "no level selected"
       would otherwise be compared as position 0 and flag a role that has no
       outcome at all the moment a ceiling sat at position 0. */
    if (levelPosition == null || levelPosition === '') return false;
    const pos = Number(levelPosition);
    if (isNaN(pos)) return false;
    return pos >= Number(ceil.levelPosition);
  };

  /**
   * The framework-wide rank of a level row (lf_level.position).
   *
   * `levelPosition` is what functionTrackLevels carries; `position` is what the
   * plain levels list carries. Deliberately does NOT fall back to `.level` —
   * that is the level NAME (a client-configured string like "4"), which is not
   * comparable across functions and would silently mis-rank the ceiling.
   */
  function positionOf(level) {
    if (!level) return null;
    const raw = level.levelPosition != null ? level.levelPosition
      : level.position != null ? level.position
      : null;
    if (raw == null) return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
  self.positionOf = positionOf;

  return self;
}
